import React, { useState, useEffect, useCallback } from 'react';
import { Loan, EquipmentType } from '../types';
import { getClientLoans } from '../services/api';
import { RefreshCw, AlertTriangle, TrendingDown, DollarSign, Calendar, Percent } from 'lucide-react';
import D3DepreciationChart from './D3DepreciationChart';

interface EquipmentListProps {
    clientId: string;
}

const EquipmentList: React.FC<EquipmentListProps> = ({ clientId }) => {
    const [loans, setLoans] = useState<Loan[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedLoans, setExpandedLoans] = useState<Set<string>>(new Set());
    const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());
    const [chartWidth, setChartWidth] = useState<number>(800);

    const loadLoans = useCallback(async (): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await getClientLoans(clientId);
            if (response.success && response.loans) {
                setLoans(response.loans);
            }
        } catch (err) {
            setError((err as Error).message || 'Failed to load loans');
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        // Calculate responsive chart width
        const updateChartWidth = () => {
            const containerWidth = Math.min(800, window.innerWidth - 100);
            setChartWidth(containerWidth);
        };

        updateChartWidth();
        window.addEventListener('resize', updateChartWidth);
        return () => window.removeEventListener('resize', updateChartWidth);
    }, []);

    useEffect(() => {
        loadLoans();
    }, [loadLoans]);

    const toggleLoanExpand = (loanId: string) => {
        setExpandedLoans(prev => {
            const newSet = new Set(prev);
            if (newSet.has(loanId)) {
                newSet.delete(loanId);
            } else {
                newSet.add(loanId);
            }
            return newSet;
        });
    };

    const toggleEquipmentExpand = (equipmentId: string) => {
        setExpandedEquipment(prev => {
            const newSet = new Set(prev);
            if (newSet.has(equipmentId)) {
                newSet.delete(equipmentId);
            } else {
                newSet.add(equipmentId);
            }
            return newSet;
        });
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatPercentage = (value: number): string => {
        return `${value.toFixed(2)}%`;
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getLoanStatusColor = (status: string): string => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'paid_off':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'defaulted':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getEquipmentTypeIcon = (type: EquipmentType): string => {
        const icons: Record<EquipmentType, string> = {
            excavator: '🔧',
            truck: '🚚',
            'CNC machine': '⚙️',
            bulldozer: '🚜',
            crane: '🏗️',
            loader: '🚛',
            compactor: '📦',
        };
        return icons[type] || '🔧';
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
                    <p className="text-gray-600">Loading loans...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                        <h5 className="font-medium text-red-900 mb-1">Error Loading Loans</h5>
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loans.length === 0) {
        return (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5" />
                    <div>
                        <h5 className="font-medium text-gray-900 mb-1">No Loans Found</h5>
                        <p className="text-sm text-gray-700">No loans are currently associated with this client.</p>
                    </div>
                </div>
            </div>
        );
    }

    const totalEquipmentCount = loans.reduce((sum, loan) => sum + loan.equipment.length, 0);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">
                    Loans ({loans.length} {loans.length === 1 ? 'loan' : 'loans'}, {totalEquipmentCount} {totalEquipmentCount === 1 ? 'equipment item' : 'equipment items'})
                </h4>
                <button
                    onClick={loadLoans}
                    className="btn btn-secondary"
                    type="button"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            <div className="grid gap-4">
                {loans.map((loan) => {
                    const isLoanExpanded = expandedLoans.has(loan.id);

                    return (
                        <div
                            key={loan.id}
                            className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                        >
                            {/* Loan Header */}
                            <div className="p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h5 className="font-semibold text-gray-900">
                                                {loan.loan_number}
                                            </h5>
                                            <span className={`text-xs px-2 py-1 rounded border capitalize ${getLoanStatusColor(loan.status)}`}>
                                                {loan.status.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <span className="text-gray-500">Loan Date:</span>
                                                    <span className="ml-1 font-medium text-gray-900">{formatDate(loan.loan_date)}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <span className="text-gray-500">Total Amount:</span>
                                                    <span className="ml-1 font-semibold text-gray-900">{formatCurrency(loan.total_amount)}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <DollarSign className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <span className="text-gray-500">Remaining:</span>
                                                    <span className="ml-1 font-semibold text-gray-900">{formatCurrency(loan.remaining_balance)}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Percent className="w-4 h-4 text-gray-400" />
                                                <div>
                                                    <span className="text-gray-500">Rate:</span>
                                                    <span className="ml-1 font-semibold text-gray-900">{formatPercentage(loan.interest_rate)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2 text-sm text-gray-600">
                                            <span>Monthly Payment: </span>
                                            <span className="font-semibold">{formatCurrency(loan.monthly_payment)}</span>
                                            <span className="ml-3">Term: </span>
                                            <span className="font-semibold">{loan.term_months} months</span>
                                            <span className="ml-3">Equipment: </span>
                                            <span className="font-semibold">{loan.equipment.length} {loan.equipment.length === 1 ? 'item' : 'items'}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => toggleLoanExpand(loan.id)}
                                        className="ml-4 text-gray-400 hover:text-gray-600"
                                        type="button"
                                    >
                                        {isLoanExpanded ? '▼' : '▶'}
                                    </button>
                                </div>
                            </div>

                            {/* Loan Equipment Details */}
                            {isLoanExpanded && (
                                <div className="border-t border-gray-200 bg-gray-50 p-4">
                                    <h6 className="font-semibold text-gray-900 mb-3">
                                        Equipment Financed ({loan.equipment.length} {loan.equipment.length === 1 ? 'item' : 'items'})
                                    </h6>
                                    <div className="space-y-3">
                                        {loan.equipment.map((item) => {
                                            const isEquipmentExpanded = expandedEquipment.has(item.id);
                                            const valueChange = item.current_value - item.original_cost;
                                            const valueChangePercent = ((valueChange / item.original_cost) * 100);

                                            return (
                                                <div
                                                    key={item.id}
                                                    className="bg-white border border-gray-200 rounded-lg"
                                                >
                                                    <div className="p-3">
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex items-start gap-3 flex-1">
                                                                <div className="text-2xl">
                                                                    {getEquipmentTypeIcon(item.equipment_type)}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <h6 className="font-semibold text-gray-900 capitalize">
                                                                            {item.equipment_type}
                                                                        </h6>
                                                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                                                            {item.year}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-600 mb-2">
                                                                        {item.make} {item.model}
                                                                    </p>
                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                                                        <div>
                                                                            <span className="text-gray-500">Serial:</span>
                                                                            <span className="ml-1 font-mono text-gray-900">{item.serial_number}</span>
                                                                        </div>
                                                                        {item.vin && (
                                                                            <div>
                                                                                <span className="text-gray-500">VIN:</span>
                                                                                <span className="ml-1 font-mono text-gray-900">{item.vin}</span>
                                                                            </div>
                                                                        )}
                                                                        <div>
                                                                            <span className="text-gray-500">Current Value:</span>
                                                                            <span className="ml-1 font-semibold text-gray-900">
                                                                                {formatCurrency(item.current_value)}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="text-gray-500">LTV:</span>
                                                                            <span className={`ml-1 font-semibold ${item.ltv > 90 ? 'text-red-600' :
                                                                                item.ltv > 75 ? 'text-orange-600' : 'text-green-600'
                                                                                }`}>
                                                                                {formatPercentage(item.ltv)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => toggleEquipmentExpand(item.id)}
                                                                className="ml-4 text-gray-400 hover:text-gray-600"
                                                                type="button"
                                                            >
                                                                {isEquipmentExpanded ? '▼' : '▶'}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {isEquipmentExpanded && (
                                                        <div className="border-t border-gray-200 bg-gray-50 p-3">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                <div className="bg-white p-2 rounded border border-gray-200">
                                                                    <div className="text-xs text-gray-500 mb-1">Original Cost</div>
                                                                    <div className="text-lg font-semibold text-gray-900">
                                                                        {formatCurrency(item.original_cost)}
                                                                    </div>
                                                                </div>
                                                                <div className="bg-white p-2 rounded border border-gray-200">
                                                                    <div className="text-xs text-gray-500 mb-1">Appraised Value</div>
                                                                    <div className="text-lg font-semibold text-gray-900">
                                                                        {formatCurrency(item.appraised_value)}
                                                                    </div>
                                                                </div>
                                                                <div className="bg-white p-2 rounded border border-gray-200">
                                                                    <div className="text-xs text-gray-500 mb-1">Current Value</div>
                                                                    <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                                        {formatCurrency(item.current_value)}
                                                                        {valueChange < 0 && (
                                                                            <TrendingDown className="w-4 h-4 text-red-600" />
                                                                        )}
                                                                    </div>
                                                                    <div className={`text-xs mt-1 ${valueChangePercent < 0 ? 'text-red-600' : 'text-gray-500'
                                                                        }`}>
                                                                        {valueChangePercent.toFixed(1)}% from original
                                                                    </div>
                                                                </div>
                                                                <div className="bg-white p-2 rounded border border-gray-200">
                                                                    <div className="text-xs text-gray-500 mb-1">Loan-to-Value (LTV)</div>
                                                                    <div className={`text-lg font-semibold ${item.ltv > 90 ? 'text-red-600' :
                                                                        item.ltv > 75 ? 'text-orange-600' : 'text-green-600'
                                                                        }`}>
                                                                        {formatPercentage(item.ltv)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Based on appraised value
                                                                    </div>
                                                                </div>
                                                                <div className="bg-white p-2 rounded border border-gray-200">
                                                                    <div className="text-xs text-gray-500 mb-1">Expected Residual Value</div>
                                                                    <div className="text-lg font-semibold text-gray-900">
                                                                        {formatCurrency(item.expected_residual_value)}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        At maturity
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {item.depreciation_curve.length > 0 && (
                                                                <div className="mt-3 bg-white p-3 rounded border border-gray-200">
                                                                    <div className="text-sm font-semibold text-gray-900 mb-3">Depreciation Curve</div>
                                                                    <D3DepreciationChart
                                                                        data={item.depreciation_curve}
                                                                        width={chartWidth}
                                                                        height={300}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default EquipmentList;

